<template>
  <div class="card">
    <div class="card-header bg-navyblue d-flex justify-content-between align-items-center">
      <span>Workflow Phases</span>
      <i
        class="fa fa-question-circle text-muted cursor-pointer workflow-phases-help"
        title="Phases group statuses together on the Kanban board"
      ></i>
    </div>
    <div class="card-block bg-white p-2">
      <div v-if="!isStatusActive" class="text-muted" style="padding: 10px 12px;">
        Read-only mode
      </div>
      <div v-if="!localPhases.length" class="text-muted" style="padding: 10px 12px;">
        No phases defined
      </div>
      <draggable
        v-model="localPhases"
        :disabled="!isStatusActive"
        ghost-class="ghost-items"
        :animation="150"
        @end="onPhaseReorder"
        class="phases-list"
      >
        <div
          v-for="(phase, index) in localPhases"
          :key="phase.phaseId"
          class="phase-row"
          @mouseover="hoveredPhaseIndex = index"
          @mouseleave="hoveredPhaseIndex = null"
        >
          <div class="phase-handle" v-if="isStatusActive">
            <i class="fa fa-bars" style="color: #d1d5db;"></i>
          </div>
          <div class="phase-name">
            <template v-if="editingPhaseId === getPhaseEditId(phase, index)">
              <span class="phase-number">{{ index + 1 }}.</span>
              <input
                ref="phaseEditInput"
                v-model="editingPhaseName"
                type="text"
                class="form-control phase-edit-input"
                @keyup.enter="savePhaseEdit(phase)"
                @keyup.esc="cancelPhaseEdit"
                @blur="savePhaseEdit(phase)"
              />
            </template>
            <template v-else>
              <span class="phase-number">{{ index + 1 }}.</span>
              {{ phase.name }}
            </template>
          </div>
          <div
            class="phase-actions"
            :class="{ 'phase-actions-visible': hoveredPhaseIndex === index && isStatusActive }"
          >
            <i class="fa fa-pencil phase-edit-icon" @click="editPhase(phase, index)"></i>
            <i
              class="fa fa-trash-o phase-delete-icon"
              @click="deletePhase(phase)"
              :class="{ 'disabled': !phase.editable }"
            ></i>
          </div>
        </div>
      </draggable>
      <div class="phase-add-footer" v-if="isStatusActive">
        <input
          v-model="newPhaseName"
          type="text"
          class="form-control phase-add-input"
          placeholder="Add new phase name"
          @keyup.enter="addPhase"
        />
        <button class="btn btn-add-phase" @click="addPhase">
          Add
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Axios from 'axios';
import { mapGetters, mapMutations, mapActions } from 'vuex';

export default {
  name: 'WorkflowPhases',
  components: {
    draggable,
  },
  props: {
    phases: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localPhases: [],
      hoveredPhaseIndex: null,
      newPhaseName: '',
      editingPhaseId: null,
      editingPhaseName: '',
    };
  },
  computed: {
    ...mapGetters({
      isStatusActive: 'isStatusActive',
    }),
  },
  watch: {
    phases: {
      immediate: true,
      handler(newVal) {
        this.localPhases = [...(newVal || [])];
      },
    },
  },
  methods: {
    ...mapMutations(['loadPhases', 'loadPhasesAndStatuses', 'resetPhaseIdForDeletedPhase']),
    ...mapActions(['loadCEStatuses']),
    onPhaseReorder() {
      // Update sort order based on array index
      const phasesData = this.localPhases.map((phase, index) => ({
        phaseId: phase.phaseId,
        name: phase.name,
        sort: index + 1,
      }));

      this.savePhases(phasesData);
    },
    addPhase() {
      if (!this.newPhaseName.trim()) {
        return;
      }

      const newPhase = {
        phaseId: null,
        name: this.newPhaseName.trim(),
        sort: this.localPhases.length + 1,
        new: true,
      };

      this.localPhases.push(newPhase);
      this.newPhaseName = '';

      this.savePhases();
    },
    getPhaseEditId(phase, index) {
      return phase.phaseId || `new-${index}`;
    },
    editPhase(phase, index) {
      this.editingPhaseId = this.getPhaseEditId(phase, index);
      this.editingPhaseName = phase.name;

      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.phaseEditInput)
          ? this.$refs.phaseEditInput[0]
          : this.$refs.phaseEditInput;

        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    savePhaseEdit(phase) {
      const newName = this.editingPhaseName.trim();

      if (!this.editingPhaseId) {
        return;
      }

      if (!newName) {
        this.cancelPhaseEdit();
        return;
      }

      if (phase.name !== newName) {
        phase.name = newName;
        this.savePhases();
      }

      this.cancelPhaseEdit();
    },
    cancelPhaseEdit() {
      this.editingPhaseId = null;
      this.editingPhaseName = '';
    },
    deletePhase(phase) {
      if (!phase.editable) {
        toastr.error('Cannot delete default phase');
        return;
      }

      if (confirm(`Are you sure you want to delete "${phase.name}"?`)) {
        Axios.post('/fe/settings/delete-phase', { phaseId: phase.phaseId })
          .then(response => {
            if (response.data && response.data._status) {
              const updatedPhases = response.data.phases || this.localPhases.filter(localPhase => localPhase.phaseId !== phase.phaseId);
              this.localPhases = [...updatedPhases];
              // Use loadPhasesAndStatuses if response contains statuses, otherwise use loadPhases
              if (response.data.statuses) {
                this.loadPhasesAndStatuses({
                  phases: updatedPhases,
                  ce_statuses: response.data.statuses.map(s => ({
                    ...s,
                    ce_status_id: s.statusId,
                    phaseId: s.phaseId === 0 ? null : s.phaseId,
                    phaseName: s.phaseName === '' ? null : s.phaseName
                  }))
                });
                toastr.success('Phase deleted successfully');
                this.$emit('phases-updated', {
                  phases: updatedPhases,
                  deletedPhaseId: phase.phaseId,
                  statuses: response.data.statuses.map(s => ({
                    ...s,
                    phaseId: s.phaseId === 0 ? null : s.phaseId,
                    phaseName: s.phaseName === '' ? null : s.phaseName
                  }))
                });
              } else {
                this.loadPhases(updatedPhases);
                this.resetPhaseIdForDeletedPhase(phase.phaseId);
                toastr.success('Phase deleted successfully');
                this.$emit('phases-updated', {
                  phases: updatedPhases,
                  deletedPhaseId: phase.phaseId,
                });
              }
            } else {
              toastr.error(response.data._msg || 'Cannot delete phase');
            }
          })
          .catch(error => {
            console.error('Failed to delete phase:', error);
            toastr.error('Failed to delete phase');
          });
      }
    },
    savePhases(phasesData = null) {
      const data = phasesData || this.localPhases.map((phase, index) => ({
        phaseId: phase.phaseId,
        name: phase.name,
        sort: index + 1,
        new: phase.new,
      }));

      NProgress.start();
      Axios.post('/fe/settings/phases', { phases: data })
        .then(response => {
          if (response.data && response.data._status) {
            const updatedPhases = response.data.phases || data;
            this.localPhases = [...updatedPhases];
            this.loadPhases(updatedPhases);
            // Reload ceStatuses to update workflow board with new phase data
            this.loadCEStatuses();
            toastr.success('Phases saved successfully');
            this.$emit('phases-updated', updatedPhases);
          } else {
            toastr.error(response.data._msg || 'Failed to save phases');
          }
        })
        .catch(error => {
          console.error('Failed to save phases:', error);
          toastr.error('Failed to save phases');
        })
        .finally(() => {
          NProgress.done();
        });
    },
  },
};
</script>

<style scoped>
.card-header {
  position: relative;
}

.workflow-phases-help {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  font-size: 14px;
  color: #9ca3af !important;
  opacity: 1;
}

.phases-list {
  min-height: 50px;
}

.phase-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: background-color 0.2s, border-color 0.2s;
  margin-bottom: 2px;
}

.phase-row:hover {
  background-color: #f8f9fa;
  border-color: #e9ecee;
}

.phase-handle {
  margin-right: 12px;
  cursor: move;
  font-size: 14px;
}

.phase-name {
  flex: 1;
  font-family: "Nunito Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #1c1f39;
}

.phase-number {
  font-weight: 600;
}

.phase-edit-input {
  display: inline-block;
  width: calc(100% - 24px);
  height: 28px;
  margin-left: 4px;
  padding: 4px 8px;
  font-size: 13px;
}

.phase-actions {
  display: flex;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s;
}

.phase-actions-visible {
  opacity: 1;
}

.phase-edit-icon,
.phase-delete-icon {
  cursor: pointer;
  font-size: 14px;
  color: #9ca3af;
  transition: color 0.2s;
}

.phase-edit-icon:hover {
  color: #3e70c9;
}

.phase-delete-icon:hover {
  color: #dc3545;
}

.phase-delete-icon.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.phase-add-footer {
  display: flex;
  gap: 8px;
  padding: 12px;
  margin: 0 -8px -8px -8px;
  border-top: 1px solid #f3f4f6;
  background-color: #f9fafb;
}

.phase-add-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 13px;
  color: #374151;
}

.phase-add-input:focus {
  border-color: #556ee6;
  outline: none;
}

.btn-add-phase {
  color: #fff;
  background-color: #5E79FF;
  border-color: #5E79FF;
  font-weight: bold;
  border-radius: 3px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: none;
  background: linear-gradient(135deg, rgb(114, 103, 231) 0%, rgb(98, 89, 239) 49.12%, rgb(78, 69, 238) 100%);
}

.btn-add-phase:hover {
  background: #5e78ff;
  border-color: #5e78ff;
}

.ghost-items {
  opacity: 0.4;
  background-color: #f8f9fa;
}
</style>
